/**
 * Chat management — sync + filter surface for Teams / Google Chat / Slack.
 *
 * Mirrors the V1 ``app/components/host.ts`` ``chatManagement`` block plus
 * the typed payloads from ``app/types/chat.ts``.
 *
 * Provider-specific filter fields are kept as optional fields on the
 * single ``ChatSyncFilters`` shape (DRY); the BE only reads the ones it
 * cares about per provider — Teams gets ``teams_ids`` / ``channel_ids``,
 * Google Chat gets ``space_ids`` / ``space_types`` / ``exclude_inactive_days``,
 * Slack reads ``include_private_chats`` / ``include_channels``.
 */

import { apiClient } from "../ApiClient.js";

export type ChatProvider = "teams" | "slack" | "google_chat";

export type ChatTimeRange = "7d" | "30d" | "90d" | "custom";

export type GoogleChatSpaceType = "SPACE" | "GROUP_CHAT" | "DIRECT_MESSAGE";

// ──────────────────────────────────────────────────────────────────────
// Filters payload (DRY across providers)
// ──────────────────────────────────────────────────────────────────────

export interface ChatSyncFilters {
  readonly time_range: ChatTimeRange;
  /** ISO YYYY-MM-DD. Required when ``time_range === "custom"``. */
  readonly custom_start_date?: string;
  readonly custom_end_date?: string;

  // Teams-specific (other providers leave undefined)
  readonly teams_ids?: readonly string[];
  readonly channel_ids?: readonly string[];

  // Slack + Teams shared
  readonly include_private_chats?: boolean;
  readonly include_channels?: boolean;

  // Google Chat-specific
  readonly space_ids?: readonly string[];
  readonly space_types?: readonly GoogleChatSpaceType[];
  readonly exclude_inactive_days?: number;

  // Common ceilings — V1 production-tuned defaults are
  // ``max_conversations=500`` / ``max_messages_per_conversation=2000``.
  readonly max_conversations: number;
  readonly max_messages_per_conversation?: number;
}

export interface ChatSyncWithFiltersBody {
  readonly user_id: string;
  readonly provider: ChatProvider;
  readonly filters: ChatSyncFilters;
  /** Multi-account workspace key (Slack; optional elsewhere). */
  readonly connection_key?: string;
}

export interface ChatSyncJobAck {
  readonly job_id?: string | null;
  readonly status?: string;
  readonly detail?: string;
}

/** POST ``/chat-management/sync-with-filters`` — main sync trigger. */
export async function syncChatWithFilters(
  body: ChatSyncWithFiltersBody,
): Promise<ChatSyncJobAck> {
  return apiClient.post<ChatSyncJobAck>(
    "/chat-management/sync-with-filters",
    body,
  );
}

// ──────────────────────────────────────────────────────────────────────
// Preview
// ──────────────────────────────────────────────────────────────────────

export interface ChatPreviewSyncResponse {
  readonly estimated_conversations: number;
  readonly estimated_messages: number;
  readonly estimated_sync_time_seconds?: number;
}

/** POST ``/chat-management/preview-sync``. */
export async function previewChatSync(
  body: ChatSyncWithFiltersBody,
): Promise<ChatPreviewSyncResponse> {
  return apiClient.post<ChatPreviewSyncResponse>(
    "/chat-management/preview-sync",
    body,
  );
}

// ──────────────────────────────────────────────────────────────────────
// Force / cancel / status
// ──────────────────────────────────────────────────────────────────────

/** GET ``/chat-management/force-sync?user_id=&provider=&max_conversations=``. */
export async function forceChatSync(
  userId: string,
  provider: ChatProvider,
  maxConversations = 500,
): Promise<ChatSyncJobAck> {
  const params = new URLSearchParams({
    user_id: userId,
    provider,
    max_conversations: String(maxConversations),
  });
  return apiClient.get<ChatSyncJobAck>(
    `/chat-management/force-sync?${params.toString()}`,
  );
}

export interface ChatSyncStatus {
  readonly status?: string;
  readonly job_id?: string | null;
  readonly job_status?: string | null;
  readonly conversations_synced?: number;
  readonly messages_synced?: number;
  readonly last_sync_at?: string | null;
}

/** GET ``/chat-management/sync-status?user_id=&provider=[&connection_key=]``. */
export async function getChatSyncStatus(
  userId: string,
  provider: ChatProvider,
  connectionKey?: string,
): Promise<ChatSyncStatus> {
  const params = new URLSearchParams({ user_id: userId, provider });
  if (connectionKey) params.set("connection_key", connectionKey);
  return apiClient.get<ChatSyncStatus>(
    `/chat-management/sync-status?${params.toString()}`,
  );
}

/** POST ``/chat-management/cancel-sync?job_id=&user_id=``. */
export async function cancelChatSync(
  jobId: string,
  userId: string,
): Promise<ChatSyncJobAck> {
  const params = new URLSearchParams({ job_id: jobId, user_id: userId });
  return apiClient.post<ChatSyncJobAck>(
    `/chat-management/cancel-sync?${params.toString()}`,
    {},
  );
}

// ──────────────────────────────────────────────────────────────────────
// Pre-sync data (teams + spaces lookups)
// ──────────────────────────────────────────────────────────────────────

export interface TeamDTO {
  readonly id: string;
  readonly name: string;
  readonly description?: string | null;
}

export interface ChannelDTO {
  readonly id: string;
  readonly team_id: string;
  readonly name: string;
  readonly type?: string;
}

export interface ListTeamsResponse {
  readonly teams: readonly TeamDTO[];
  readonly channels: readonly ChannelDTO[];
}

/** GET ``/chat-management/list-teams?user_id=`` (Teams provider). */
export async function listChatTeams(
  userId: string,
): Promise<ListTeamsResponse> {
  return apiClient.get<ListTeamsResponse>(
    `/chat-management/list-teams?user_id=${encodeURIComponent(userId)}`,
  );
}

export interface SpaceDTO {
  readonly id: string;
  readonly name: string;
  readonly type: GoogleChatSpaceType;
  readonly member_count?: number;
  readonly last_active_at?: string | null;
}

export interface ListSpacesResponse {
  readonly spaces: readonly SpaceDTO[];
  readonly by_type: Readonly<Record<GoogleChatSpaceType, readonly SpaceDTO[]>>;
}

/** GET ``/chat-management/list-spaces?user_id=`` (Google Chat provider). */
export async function listChatSpaces(
  userId: string,
): Promise<ListSpacesResponse> {
  return apiClient.get<ListSpacesResponse>(
    `/chat-management/list-spaces?user_id=${encodeURIComponent(userId)}`,
  );
}

// ──────────────────────────────────────────────────────────────────────
// Connections list (used by the chat sync UI to seed the account picker)
// ──────────────────────────────────────────────────────────────────────

export interface ChatConnection {
  readonly provider: ChatProvider;
  readonly connection_key?: string | null;
  readonly display_name?: string | null;
  readonly connected: boolean;
  readonly last_used_at?: string | null;
  readonly token_status?: string | null;
}

export interface ChatConnectionsResponse {
  readonly connections: readonly ChatConnection[];
}

/** GET ``/chat-management/connections?user_id=``. */
export async function listChatConnections(
  userId: string,
): Promise<readonly ChatConnection[]> {
  const res = await apiClient.get<ChatConnectionsResponse>(
    `/chat-management/connections?user_id=${encodeURIComponent(userId)}`,
  );
  return res.connections;
}


export interface MeetingTranscriptImportResult {
  readonly found: number;
  readonly ingested: number;
  readonly skipped: number;
  readonly errors: readonly string[];
}

/** Import historical Meet transcripts for the explicitly selected Google account. */
export async function importGoogleMeetTranscripts(
  connectionKey: string,
  sinceDays: 7 | 30 | 90 = 30,
): Promise<MeetingTranscriptImportResult> {
  if (!connectionKey.trim()) throw new Error("Choose a Google account");
  const params = new URLSearchParams({
    provider: "google_chat", connection_key: connectionKey, since_days: String(sinceDays),
  });
  return apiClient.post<MeetingTranscriptImportResult>(
    `/chat-management/ingest-transcripts?${params.toString()}`, {},
    { timeout: 300_000 },
  );
}


export type MeetingTranscriptJobStatus = "pending" | "processing" | "completed" | "completed_with_errors" | "failed" | "cancelled";
export interface MeetingTranscriptJobRef {
  readonly job_id: string;
  readonly status: MeetingTranscriptJobStatus;
}
export interface MeetingTranscriptJobStart extends MeetingTranscriptJobRef { readonly reused: boolean; }
export interface MeetingTranscriptJobProgress extends MeetingTranscriptJobRef {
  readonly error?: string | null;
  readonly transcript_import: {
    readonly provider: "google_chat";
    readonly connection_key: string;
    readonly since_days: number;
    readonly found: number | null;
    readonly ingested: number;
    readonly skipped: number;
    readonly errors: readonly string[];
  };
}
function transcriptJobParams(connectionKey: string, sinceDays: 7 | 30 | 90) {
  if (!connectionKey.trim()) throw new Error("Choose a Google account");
  return { provider: "google_chat" as const, connection_key: connectionKey, since_days: sinceDays };
}
/** Queue only after an explicit user action; the authenticated owner is server-bound. */
export async function startGoogleMeetTranscriptJob(connectionKey: string, sinceDays: 7 | 30 | 90 = 30): Promise<MeetingTranscriptJobStart> {
  return apiClient.post<MeetingTranscriptJobStart>("/chat-management/transcript-import-jobs", transcriptJobParams(connectionKey, sinceDays));
}
/** Recover the selected account/window's durable job after closing or reloading the drawer. */
export async function findGoogleMeetTranscriptJob(connectionKey: string, sinceDays: 7 | 30 | 90 = 30): Promise<{ readonly job: MeetingTranscriptJobRef | null }> {
  const body = transcriptJobParams(connectionKey, sinceDays);
  const params = new URLSearchParams({ ...body, since_days: String(body.since_days) });
  return apiClient.get(`/chat-management/transcript-import-jobs?${params.toString()}`);
}
export async function getGoogleMeetTranscriptJobProgress(jobId: string): Promise<MeetingTranscriptJobProgress> {
  return apiClient.get(`/jobs/${encodeURIComponent(jobId)}/progress`);
}
